/**
 * BulkUploadZone Component
 *
 * Handles bulk upload of MCPs from MCP Maker output folder.
 * - Accepts folder drop, detects registry.json
 * - Parses registry, diffs against existing MCPs
 * - Shows preview with counts
 * - Sequential upload with rate limiting
 * - Progress persistence to localStorage
 * - Retry failed uploads
 *
 * Usage: Import into MCPAdminTab.js and render in mcps tab
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { authFetch, apiUrl } from "../../../utils/api";

// Rate limiting constants (from spec)
const UPLOAD_DELAY_MS = 500;
const BATCH_SIZE = 50;
const BATCH_PAUSE_MS = 5000;
const MAX_RETRIES = 3;

// Category to emoji mapping
const CATEGORY_ICONS = {
  crm: "👥",
  productivity: "📋",
  google: "🔵",
  media: "🎬",
  events: "📅",
  messaging: "💬",
  finance: "💰",
  automation: "⚡",
  analytics: "📊",
  devops: "🔧",
  social: "📱",
  email: "📧",
  sales: "💰",
  marketing: "📣",
  other: "🔌",
};

const STORAGE_KEY = "mcp_bulk_upload_progress";
const LAST_FOLDER_KEY = "mcp_bulk_upload_last_folder";
const SESSION_KEY = "mcp_bulk_upload_session";

// Update mode options
const UPDATE_MODES = {
  NEW_ONLY: "new_only", // Only upload MCPs that don't exist (default)
  SMART_UPDATE: "smart_update", // Compare hashes - only upload what changed (code, reqs, or both)
  REQUIREMENTS_ONLY: "requirements_only", // Only update requirements.txt for existing MCPs
  CODE_ONLY: "code_only", // Only update server.py for existing MCPs
  FULL_UPDATE: "full_update", // Re-upload everything (creates new versions)
};

const BulkUploadZone = ({ onUploadComplete, showMessage }) => {
  // Update mode selection
  const [updateMode, setUpdateMode] = useState(UPDATE_MODES.NEW_ONLY);

  // Track if we have restored state that needs explicit user action to resume
  const [hasRestoredState, setHasRestoredState] = useState(false);

  // Unique session ID to prevent multi-tab upload conflicts
  const sessionIdRef = useRef(null);

  // Upload state
  const [uploadState, setUploadState] = useState({
    status: "idle", // idle | parsing | diffing | ready | uploading | paused | complete | restored
    registry: null,
    folderHandle: null,
    toUpload: [], // New MCPs or full updates
    toUpdateCode: [], // MCPs that need code update only (server.py changed)
    toUpdateReqs: [], // MCPs that need requirements update only
    toUpdateBoth: [], // MCPs that need both code and requirements update
    skipped: [],
    completed: [],
    failed: [],
    currentIndex: 0,
    forceUpdate: false,
    currentMcpName: "",
    error: null,
    dbHashes: null, // Cached DB hashes for comparison
    uploadQueue: [], // Combined queue for upload iteration (keeps category counts intact)
  });

  // Folder contents cache (file handles from drop)
  // Using ref to avoid stale closure issues in readFileFromFolder
  const folderContentsRef = useRef({});
  const [folderContents, setFolderContents] = useState({});

  // Keep ref in sync with state
  useEffect(() => {
    folderContentsRef.current = folderContents;
  }, [folderContents]);

  // Upload state ref to avoid stale closures in async loops
  const uploadStateRef = useRef(uploadState);
  useEffect(() => {
    uploadStateRef.current = uploadState;
  }, [uploadState]);

  // Generate unique session ID on mount
  useEffect(() => {
    sessionIdRef.current = `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    console.log(`[BulkUpload] Session started: ${sessionIdRef.current}`);

    // Cleanup on unmount - clear session if it's ours
    return () => {
      const activeSession = localStorage.getItem(SESSION_KEY);
      if (activeSession === sessionIdRef.current) {
        console.log(`[BulkUpload] Session ended: ${sessionIdRef.current}`);
        localStorage.removeItem(SESSION_KEY);
      }
    };
  }, []);

  // Check for saved progress on mount - NEVER auto-resume, always require explicit user action
  useEffect(() => {
    const savedProgress = localStorage.getItem(STORAGE_KEY);
    if (savedProgress) {
      try {
        const parsed = JSON.parse(savedProgress);
        // Only restore if not completed and has meaningful progress
        if (
          (parsed.status === "uploading" || parsed.status === "paused") &&
          parsed.uploadQueue &&
          parsed.uploadQueue.length > 0
        ) {
          console.log(
            `[BulkUpload] Found saved progress from ${parsed.savedAt}, marking as restored`,
          );
          setHasRestoredState(true);
          setUploadState((prev) => ({
            ...prev,
            ...parsed,
            status: "restored", // Special status - requires explicit user action to resume
          }));
        } else {
          // Clear stale/invalid saved state
          console.log("[BulkUpload] Clearing stale saved progress");
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch (e) {
        console.error("Failed to parse saved progress:", e);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  // Save progress to localStorage whenever state changes during upload
  useEffect(() => {
    if (uploadState.status === "uploading" || uploadState.status === "paused") {
      const toSave = {
        status: uploadState.status,
        toUpload: uploadState.toUpload,
        toUpdateCode: uploadState.toUpdateCode,
        toUpdateReqs: uploadState.toUpdateReqs,
        toUpdateBoth: uploadState.toUpdateBoth,
        uploadQueue: uploadState.uploadQueue,
        skipped: uploadState.skipped,
        completed: uploadState.completed,
        failed: uploadState.failed,
        currentIndex: uploadState.currentIndex,
        forceUpdate: uploadState.forceUpdate,
        savedAt: new Date().toISOString(),
        sessionId: sessionIdRef.current,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
      // Also track active session
      localStorage.setItem(SESSION_KEY, sessionIdRef.current);
    } else if (
      uploadState.status === "complete" ||
      uploadState.status === "idle"
    ) {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(SESSION_KEY);
    }
  }, [uploadState]);

  /**
   * Sleep helper
   */
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  /**
   * Get icon for category
   */
  const getIcon = (category) => CATEGORY_ICONS[category] || "🔌";

  /**
   * Recursively read all entries from a directory
   */
  const readAllEntries = async (dirHandle, path = "") => {
    const entries = {};

    for await (const entry of dirHandle.values()) {
      const entryPath = path ? `${path}/${entry.name}` : entry.name;

      if (entry.kind === "file") {
        entries[entryPath] = entry;
      } else if (entry.kind === "directory") {
        const subEntries = await readAllEntries(entry, entryPath);
        Object.assign(entries, subEntries);
      }
    }

    return entries;
  };

  /**
   * Handle folder drop
   */
  const handleFolderDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    setUploadState((prev) => ({ ...prev, status: "parsing", error: null }));

    try {
      // Get the dropped items
      const items = e.dataTransfer.items;

      if (!items || items.length === 0) {
        throw new Error("No items dropped");
      }

      // Find directory entry
      let dirHandle = null;
      for (const item of items) {
        if (item.kind === "file") {
          const entry = await item.getAsFileSystemHandle();
          if (entry.kind === "directory") {
            dirHandle = entry;
            break;
          }
        }
      }

      if (!dirHandle) {
        throw new Error(
          "Please drop a folder, not individual files. Drop the entire MCP Maker output folder.",
        );
      }

      console.log("[BulkUpload] Reading folder:", dirHandle.name);

      // Read all entries recursively
      const entries = await readAllEntries(dirHandle);
      setFolderContents(entries);

      console.log("[BulkUpload] Found entries:", Object.keys(entries).length);

      // Find registry.json - prioritize root-level over subdirectory registries
      const allRegistryPaths = Object.keys(entries).filter(
        (p) => p === "registry.json" || p.endsWith("/registry.json"),
      );
      // Sort by path depth (fewer slashes = closer to root)
      allRegistryPaths.sort((a, b) => {
        const depthA = (a.match(/\//g) || []).length;
        const depthB = (b.match(/\//g) || []).length;
        return depthA - depthB;
      });
      const registryPath = allRegistryPaths[0];

      if (!registryPath) {
        throw new Error(
          "No registry.json found in folder. Make sure you're dropping the MCP Maker output folder.",
        );
      }

      // Read and parse registry.json
      const registryFile = await entries[registryPath].getFile();
      const registryText = await registryFile.text();
      const registry = JSON.parse(registryText);

      console.log("[BulkUpload] Registry parsed:", {
        version: registry.version,
        serverCount: Object.keys(registry.servers || {}).length,
      });

      if (!registry.servers || Object.keys(registry.servers).length === 0) {
        throw new Error("registry.json has no servers defined");
      }

      // Now diff against existing MCPs
      setUploadState((prev) => ({ ...prev, status: "diffing", registry }));

      const existingMcps = await fetchExistingMcps();
      const existingMap = new Map(
        existingMcps.map((m) => [m.name.toLowerCase(), m]),
      );

      // For SMART_UPDATE mode, fetch DB hashes for comparison
      let dbHashes = {};
      if (updateMode === UPDATE_MODES.SMART_UPDATE) {
        dbHashes = await fetchDbHashes();
        console.log("[BulkUpload] DB hashes fetched for smart comparison");
      }

      // Separate into categories based on mode and hash comparison
      const toUpload = []; // New MCPs or full re-uploads
      const toUpdateCode = []; // Only server.py changed
      const toUpdateReqs = []; // Only requirements.txt changed
      const toUpdateBoth = []; // Both changed
      const skipped = []; // No changes or skipped by mode

      for (const [key, mcp] of Object.entries(registry.servers)) {
        const mcpData = {
          key,
          ...mcp,
          // Ensure we have the path
          path: mcp.path || `${key}/server.py`,
        };

        const existing = existingMap.get(key.toLowerCase());

        if (existing) {
          mcpData.existingId = existing.id;

          if (updateMode === UPDATE_MODES.SMART_UPDATE) {
            // Compare hashes to determine what changed
            const dbHash = dbHashes[key.toLowerCase()];
            const registryCodeHash = mcp.server_py_hash;
            const registryReqsHash = mcp.requirements_hash;

            if (!dbHash) {
              // No hash in DB - treat as new (shouldn't happen often)
              console.log(
                `[BulkUpload] No DB hash for ${key}, treating as new`,
              );
              toUpload.push(mcpData);
            } else {
              const codeChanged =
                registryCodeHash && dbHash.server_py_hash !== registryCodeHash;
              const reqsChanged =
                registryReqsHash &&
                dbHash.requirements_hash !== registryReqsHash;

              if (codeChanged && reqsChanged) {
                mcpData.changeType = "both";
                toUpdateBoth.push(mcpData);
              } else if (codeChanged) {
                mcpData.changeType = "code";
                toUpdateCode.push(mcpData);
              } else if (reqsChanged) {
                mcpData.changeType = "requirements";
                toUpdateReqs.push(mcpData);
              } else {
                // No changes - skip
                mcpData.changeType = "unchanged";
                skipped.push(mcpData);
              }
            }
          } else if (updateMode === UPDATE_MODES.REQUIREMENTS_ONLY) {
            // Queue for requirements-only update
            toUpdateReqs.push(mcpData);
          } else if (updateMode === UPDATE_MODES.CODE_ONLY) {
            // Queue for code-only update
            toUpdateCode.push(mcpData);
          } else if (updateMode === UPDATE_MODES.FULL_UPDATE) {
            // Queue for full re-upload (creates new version)
            toUpload.push(mcpData);
          } else {
            // NEW_ONLY mode - skip existing
            skipped.push(mcpData);
          }
        } else {
          // New MCP - always upload
          mcpData.changeType = "new";
          toUpload.push(mcpData);
        }
      }

      console.log("[BulkUpload] Diff complete:", {
        mode: updateMode,
        toUpload: toUpload.length,
        toUpdateCode: toUpdateCode.length,
        toUpdateReqs: toUpdateReqs.length,
        toUpdateBoth: toUpdateBoth.length,
        skipped: skipped.length,
      });

      setUploadState((prev) => ({
        ...prev,
        status: "ready",
        registry,
        toUpload,
        toUpdateCode,
        toUpdateReqs,
        toUpdateBoth,
        skipped,
        dbHashes,
        completed: [],
        failed: [],
        currentIndex: 0,
      }));
    } catch (error) {
      console.error("[BulkUpload] Error:", error);
      setUploadState((prev) => ({
        ...prev,
        status: "idle",
        error: error.message,
      }));
      if (showMessage) {
        showMessage("error", error.message);
      }
    }
  };

  /**
   * Fetch existing MCPs from API
   */
  const fetchExistingMcps = async () => {
    try {
      const response = await authFetch(apiUrl("/api/mcp/list"));
      const data = await response.json();
      return data.mcps || [];
    } catch (error) {
      console.error("[BulkUpload] Failed to fetch existing MCPs:", error);
      return [];
    }
  };

  /**
   * Fetch hashes for all MCPs from Gateway for change detection
   */
  const fetchDbHashes = async () => {
    try {
      const response = await authFetch(apiUrl("/api/mcp/hashes"));
      const data = await response.json();
      if (data.success) {
        console.log(`[BulkUpload] Fetched hashes for ${data.count} MCPs`);
        return data.hashes || {};
      }
      return {};
    } catch (error) {
      console.error("[BulkUpload] Failed to fetch DB hashes:", error);
      return {};
    }
  };

  /**
   * Read file content from folder
   */
  const readFileFromFolder = async (relativePath) => {
    // Use ref to get current folder contents (avoids stale closure)
    const contents = folderContentsRef.current;

    // Try exact path first
    let fileHandle = contents[relativePath];

    // If not found, try without leading slash or with different structure
    if (!fileHandle) {
      const normalizedPath = relativePath.replace(/^\/+/, "");
      fileHandle = contents[normalizedPath];
    }

    // Try finding by filename in any subdirectory
    if (!fileHandle) {
      const filename = relativePath.split("/").pop();
      const matchingPath = Object.keys(contents).find(
        (p) => p.endsWith(`/${filename}`) || p === filename,
      );
      if (matchingPath) {
        fileHandle = contents[matchingPath];
      }
    }

    if (!fileHandle) {
      console.log(`[BulkUpload] File not found: ${relativePath}`);
      console.log(
        `[BulkUpload] Available paths:`,
        Object.keys(contents).slice(0, 20),
      );
      return null;
    }

    const file = await fileHandle.getFile();
    return await file.text();
  };

  /**
   * Upload single MCP with retry logic
   */
  const uploadSingleMcp = async (mcp, retryCount = 0) => {
    try {
      // Determine file paths
      const mcpFolder = mcp.key;
      const serverPyPath = mcp.path || `${mcpFolder}/server.py`;
      // Extract directory from server.py path to find requirements.txt in same folder
      const serverDir = serverPyPath.substring(
        0,
        serverPyPath.lastIndexOf("/"),
      );
      const requirementsPath = `${serverDir}/requirements.txt`;

      // Read server.py
      const serverPyContent = await readFileFromFolder(serverPyPath);
      if (!serverPyContent) {
        throw new Error(`server.py not found at ${serverPyPath}`);
      }

      // Read requirements.txt (optional)
      const requirementsContent = await readFileFromFolder(requirementsPath);

      // Create form data
      const formData = new FormData();

      // Create file blobs
      const serverPyBlob = new Blob([serverPyContent], {
        type: "text/x-python",
      });
      formData.append("server_py", serverPyBlob, "server.py");

      if (requirementsContent) {
        const reqBlob = new Blob([requirementsContent], { type: "text/plain" });
        formData.append("requirements", reqBlob, "requirements.txt");
      }

      // Add metadata
      formData.append("name", mcp.key);
      formData.append("display_name", mcp.name || mcp.key);
      formData.append("description", mcp.description || "");
      formData.append("category", mcp.category || "other");
      formData.append("icon", getIcon(mcp.category));

      // Upload
      const response = await authFetch(apiUrl("/api/mcp/upload"), {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (!response.ok || data.error) {
        throw new Error(
          data.error?.issues?.join(", ") ||
            data.error ||
            data.detail?.error ||
            "Upload failed",
        );
      }

      return { success: true, data };
    } catch (error) {
      // Retry logic
      if (retryCount < MAX_RETRIES - 1) {
        console.log(
          `[BulkUpload] Retry ${retryCount + 1}/${MAX_RETRIES} for ${mcp.key}`,
        );
        await sleep(1000 * (retryCount + 1)); // Exponential backoff
        return uploadSingleMcp(mcp, retryCount + 1);
      }

      return { success: false, error: error.message };
    }
  };

  /**
   * Bulk update requirements.txt for multiple MCPs
   */
  const bulkUpdateRequirements = async () => {
    const { toUpdateReqs } = uploadState;

    if (!toUpdateReqs || toUpdateReqs.length === 0) {
      showMessage?.("info", "No MCPs to update");
      return;
    }

    setUploadState((prev) => ({
      ...prev,
      status: "uploading",
      currentMcpName: "Preparing requirements updates...",
    }));

    // Build the updates array
    const updates = [];
    const failedReads = [];

    for (const mcp of toUpdateReqs) {
      const mcpFolder = mcp.key;
      const serverPyPath = mcp.path || `${mcpFolder}/server.py`;
      const serverDir = serverPyPath.substring(
        0,
        serverPyPath.lastIndexOf("/"),
      );
      const requirementsPath = `${serverDir}/requirements.txt`;

      const requirementsContent = await readFileFromFolder(requirementsPath);

      if (requirementsContent) {
        updates.push({
          mcp_id: mcp.existingId,
          requirements_content: requirementsContent,
        });
      } else {
        failedReads.push(mcp.key);
      }
    }

    if (updates.length === 0) {
      setUploadState((prev) => ({
        ...prev,
        status: "complete",
        failed: failedReads.map((k) => ({
          key: k,
          error: "No requirements.txt found",
        })),
      }));
      showMessage?.("error", "No requirements.txt files found to update");
      return;
    }

    setUploadState((prev) => ({
      ...prev,
      currentMcpName: `Updating ${updates.length} MCPs...`,
    }));

    try {
      // Call the bulk update endpoint
      const response = await authFetch(
        apiUrl("/api/mcp/bulk-update-requirements"),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ updates }),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.detail || "Bulk update failed");
      }

      // Mark completed/failed
      const completedMcps = toUpdateReqs.filter(
        (m) => !data.failed_ids?.includes(m.existingId),
      );
      const failedMcps = toUpdateReqs
        .filter((m) => data.failed_ids?.includes(m.existingId))
        .map((m) => ({ ...m, error: "Update failed" }));

      setUploadState((prev) => ({
        ...prev,
        status: "complete",
        completed: completedMcps,
        failed: [
          ...failedMcps,
          ...failedReads.map((k) => ({ key: k, error: "No requirements.txt" })),
        ],
        currentMcpName: "",
      }));

      showMessage?.(
        "success",
        `Requirements updated! ${data.updated} succeeded, ${data.failed} failed`,
      );

      if (onUploadComplete) {
        onUploadComplete();
      }
    } catch (error) {
      console.error("[BulkUpload] Bulk requirements update failed:", error);
      setUploadState((prev) => ({
        ...prev,
        status: "complete",
        error: error.message,
      }));
      showMessage?.("error", `Bulk update failed: ${error.message}`);
    }
  };

  /**
   * Start or resume upload process
   */
  const startUpload = async () => {
    // Check if another tab is actively uploading
    const activeSession = localStorage.getItem(SESSION_KEY);
    if (activeSession && activeSession !== sessionIdRef.current) {
      showMessage?.(
        "error",
        "Another browser tab is already running an upload. Please close it first.",
      );
      console.warn(
        `[BulkUpload] Blocked - another session active: ${activeSession}`,
      );
      return;
    }

    // Claim the session
    localStorage.setItem(SESSION_KEY, sessionIdRef.current);
    console.log(
      `[BulkUpload] Starting upload with session: ${sessionIdRef.current}`,
    );

    // Clear restored state flag
    setHasRestoredState(false);

    // If in requirements-only mode, use the bulk update function
    if (updateMode === UPDATE_MODES.REQUIREMENTS_ONLY) {
      return bulkUpdateRequirements();
    }

    // For SMART_UPDATE mode, handle requirements-only updates via bulk endpoint first
    if (
      updateMode === UPDATE_MODES.SMART_UPDATE &&
      uploadState.toUpdateReqs.length > 0
    ) {
      console.log(
        `[BulkUpload] Processing ${uploadState.toUpdateReqs.length} requirements-only updates...`,
      );
      await bulkUpdateRequirements();
    }

    const {
      toUpload,
      toUpdateCode,
      toUpdateBoth,
      currentIndex,
      forceUpdate,
      skipped,
      uploadQueue: existingQueue,
    } = uploadState;

    // Use existing uploadQueue if resuming from saved session, otherwise build new queue
    let queue;
    if (existingQueue && existingQueue.length > 0 && currentIndex > 0) {
      // Resuming from saved session - use existing queue
      queue = existingQueue;
    } else {
      // Build new queue based on mode
      queue = [...toUpload];

      if (updateMode === UPDATE_MODES.SMART_UPDATE) {
        // Smart mode: upload new + code changes + both changes
        queue = [...toUpload, ...toUpdateCode, ...toUpdateBoth];
      } else if (updateMode === UPDATE_MODES.CODE_ONLY) {
        // Code only mode: just code updates for existing MCPs
        queue = [...toUpdateCode];
      } else if (forceUpdate && currentIndex === 0) {
        // Force update: use skipped items as the queue (toUpload is empty when all were skipped)
        queue = [...skipped];
      }
    }

    if (queue.length === 0) {
      // Nothing to upload - mark as complete
      setUploadState((prev) => ({
        ...prev,
        status: "complete",
        currentMcpName: "",
      }));
      showMessage?.("success", "No code changes to upload!");
      return;
    }

    setUploadState((prev) => ({
      ...prev,
      status: "uploading",
      uploadQueue: queue, // Store queue separately to preserve category counts for display
    }));

    for (let i = currentIndex; i < queue.length; i++) {
      // Check if paused (use ref to get current state, not stale closure)
      if (uploadStateRef.current.status === "paused") {
        return;
      }

      const mcp = queue[i];

      setUploadState((prev) => ({
        ...prev,
        currentIndex: i,
        currentMcpName: mcp.name || mcp.key,
      }));

      console.log(
        `[BulkUpload] Uploading ${i + 1}/${queue.length}: ${mcp.key}`,
      );

      const result = await uploadSingleMcp(mcp);

      setUploadState((prev) => ({
        ...prev,
        completed: result.success ? [...prev.completed, mcp] : prev.completed,
        failed: !result.success
          ? [...prev.failed, { ...mcp, error: result.error }]
          : prev.failed,
        currentIndex: i + 1,
      }));

      // Rate limiting
      await sleep(UPLOAD_DELAY_MS);

      // Batch pause
      if ((i + 1) % BATCH_SIZE === 0 && i + 1 < queue.length) {
        console.log(`[BulkUpload] Batch pause after ${i + 1} uploads...`);
        setUploadState((prev) => ({
          ...prev,
          currentMcpName: `Pausing after batch ${Math.floor((i + 1) / BATCH_SIZE)}...`,
        }));
        await sleep(BATCH_PAUSE_MS);
      }
    }

    // Complete
    setUploadState((prev) => ({
      ...prev,
      status: "complete",
      currentMcpName: "",
    }));

    // Clear saved progress
    localStorage.removeItem(STORAGE_KEY);

    // Notify parent
    if (onUploadComplete) {
      onUploadComplete();
    }

    // Use functional update to get final state for the message
    setUploadState((prev) => {
      if (showMessage) {
        showMessage(
          "success",
          `Bulk upload complete! ${prev.completed.length} succeeded, ${prev.failed.length} failed`,
        );
      }
      return prev; // Don't change state, just read it
    });
  };

  /**
   * Pause upload
   */
  const pauseUpload = () => {
    console.log(`[BulkUpload] Upload paused by user`);
    setUploadState((prev) => ({ ...prev, status: "paused" }));
  };

  /**
   * Resume upload - requires explicit user action
   */
  const resumeUpload = () => {
    console.log(`[BulkUpload] Resume requested by user`);
    // If resuming from restored state, set status to paused first so startUpload works correctly
    if (uploadState.status === "restored") {
      setUploadState((prev) => ({ ...prev, status: "paused" }));
    }
    startUpload();
  };

  /**
   * Discard restored progress without resuming
   */
  const discardRestoredProgress = () => {
    console.log(`[BulkUpload] Discarding restored progress`);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(SESSION_KEY);
    setHasRestoredState(false);
    setUploadState({
      status: "idle",
      registry: null,
      folderHandle: null,
      toUpload: [],
      toUpdateCode: [],
      toUpdateReqs: [],
      toUpdateBoth: [],
      skipped: [],
      completed: [],
      failed: [],
      currentIndex: 0,
      forceUpdate: false,
      currentMcpName: "",
      error: null,
      dbHashes: null,
      uploadQueue: [],
    });
    setFolderContents({});
    showMessage?.("info", "Previous upload progress discarded");
  };

  /**
   * Retry single failed MCP
   */
  const retrySingleMcp = async (mcp, index) => {
    const result = await uploadSingleMcp(mcp);

    setUploadState((prev) => {
      const newFailed = [...prev.failed];
      if (result.success) {
        newFailed.splice(index, 1);
        return {
          ...prev,
          completed: [...prev.completed, mcp],
          failed: newFailed,
        };
      } else {
        newFailed[index] = { ...mcp, error: result.error };
        return { ...prev, failed: newFailed };
      }
    });
  };

  /**
   * Retry all failed MCPs
   */
  const retryAllFailed = async () => {
    const failedCopy = [...uploadState.failed];
    setUploadState((prev) => ({ ...prev, status: "uploading", failed: [] }));

    for (const mcp of failedCopy) {
      const result = await uploadSingleMcp(mcp);

      setUploadState((prev) => ({
        ...prev,
        completed: result.success ? [...prev.completed, mcp] : prev.completed,
        failed: !result.success
          ? [...prev.failed, { ...mcp, error: result.error }]
          : prev.failed,
      }));

      await sleep(UPLOAD_DELAY_MS);
    }

    setUploadState((prev) => ({ ...prev, status: "complete" }));
  };

  /**
   * Reset to initial state
   */
  const resetUpload = () => {
    console.log(`[BulkUpload] Reset requested`);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(SESSION_KEY);
    setHasRestoredState(false);
    setUploadState({
      status: "idle",
      registry: null,
      folderHandle: null,
      toUpload: [],
      toUpdateCode: [],
      toUpdateReqs: [],
      toUpdateBoth: [],
      skipped: [],
      completed: [],
      failed: [],
      currentIndex: 0,
      forceUpdate: false,
      currentMcpName: "",
      error: null,
      dbHashes: null,
      uploadQueue: [],
    });
    setFolderContents({});
  };

  /**
   * Toggle force update
   */
  const toggleForceUpdate = () => {
    setUploadState((prev) => ({ ...prev, forceUpdate: !prev.forceUpdate }));
  };

  /**
   * Prevent default drag behavior
   */
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  /**
   * Handle click to browse - uses File System Access API
   */
  const handleClickToBrowse = async () => {
    try {
      // Check if showDirectoryPicker is supported
      if (!window.showDirectoryPicker) {
        if (showMessage) {
          showMessage(
            "error",
            "Your browser doesn't support folder selection. Please use drag & drop or try Chrome/Edge.",
          );
        }
        return;
      }

      setUploadState((prev) => ({ ...prev, status: "parsing", error: null }));

      // Open folder picker
      const dirHandle = await window.showDirectoryPicker({
        mode: "read",
      });

      // Save folder name for future reference
      localStorage.setItem(LAST_FOLDER_KEY, dirHandle.name);

      console.log("[BulkUpload] Selected folder:", dirHandle.name);

      // Read all entries recursively
      const entries = await readAllEntries(dirHandle);
      setFolderContents(entries);

      console.log("[BulkUpload] Found entries:", Object.keys(entries).length);

      // Look for registry.json - prioritize root-level over subdirectory registries
      const allRegistryPaths = Object.keys(entries).filter(
        (p) => p === "registry.json" || p.endsWith("/registry.json"),
      );
      // Sort by path depth (fewer slashes = closer to root)
      allRegistryPaths.sort((a, b) => {
        const depthA = (a.match(/\//g) || []).length;
        const depthB = (b.match(/\//g) || []).length;
        return depthA - depthB;
      });
      const registryPath = allRegistryPaths[0];

      if (!registryPath) {
        throw new Error(
          "No registry.json found. Please select the MCP Maker output folder that contains registry.json.",
        );
      }

      // Read registry.json - entries contains FileSystemFileHandle, need to get File first
      const registryHandle = entries[registryPath];
      const registryFile = await registryHandle.getFile();
      const registryText = await registryFile.text();
      const registry = JSON.parse(registryText);

      console.log("[BulkUpload] Registry loaded:", {
        version: registry.version,
        serverCount: Object.keys(registry.servers || {}).length,
      });

      if (!registry.servers || Object.keys(registry.servers).length === 0) {
        throw new Error("registry.json has no servers defined");
      }

      // Now diff against existing MCPs
      setUploadState((prev) => ({ ...prev, status: "diffing", registry }));

      const existingMcps = await fetchExistingMcps();
      const existingMap = new Map(
        existingMcps.map((m) => [m.name.toLowerCase(), m]),
      );

      // For SMART_UPDATE mode, fetch DB hashes for comparison
      let dbHashes = {};
      if (updateMode === UPDATE_MODES.SMART_UPDATE) {
        dbHashes = await fetchDbHashes();
        console.log("[BulkUpload] DB hashes fetched for smart comparison");
      }

      // Separate into categories based on mode and hash comparison
      const toUpload = []; // New MCPs or full re-uploads
      const toUpdateCode = []; // Only server.py changed
      const toUpdateReqs = []; // Only requirements.txt changed
      const toUpdateBoth = []; // Both changed
      const skipped = []; // No changes or skipped by mode

      for (const [key, mcp] of Object.entries(registry.servers)) {
        const mcpData = {
          key,
          ...mcp,
          path: mcp.path || `${key}/server.py`,
        };

        const existing = existingMap.get(key.toLowerCase());

        if (existing) {
          mcpData.existingId = existing.id;

          if (updateMode === UPDATE_MODES.SMART_UPDATE) {
            // Compare hashes to determine what changed
            const dbHash = dbHashes[key.toLowerCase()];
            const registryCodeHash = mcp.server_py_hash;
            const registryReqsHash = mcp.requirements_hash;

            if (!dbHash) {
              // No hash in DB - treat as new (shouldn't happen often)
              console.log(
                `[BulkUpload] No DB hash for ${key}, treating as new`,
              );
              toUpload.push(mcpData);
            } else {
              const codeChanged =
                registryCodeHash && dbHash.server_py_hash !== registryCodeHash;
              const reqsChanged =
                registryReqsHash &&
                dbHash.requirements_hash !== registryReqsHash;

              if (codeChanged && reqsChanged) {
                mcpData.changeType = "both";
                toUpdateBoth.push(mcpData);
              } else if (codeChanged) {
                mcpData.changeType = "code";
                toUpdateCode.push(mcpData);
              } else if (reqsChanged) {
                mcpData.changeType = "requirements";
                toUpdateReqs.push(mcpData);
              } else {
                // No changes - skip
                mcpData.changeType = "unchanged";
                skipped.push(mcpData);
              }
            }
          } else if (updateMode === UPDATE_MODES.REQUIREMENTS_ONLY) {
            // Queue for requirements-only update
            toUpdateReqs.push(mcpData);
          } else if (updateMode === UPDATE_MODES.CODE_ONLY) {
            // Queue for code-only update
            toUpdateCode.push(mcpData);
          } else if (updateMode === UPDATE_MODES.FULL_UPDATE) {
            // Queue for full re-upload (creates new version)
            toUpload.push(mcpData);
          } else {
            // NEW_ONLY mode - skip existing
            skipped.push(mcpData);
          }
        } else {
          // New MCP - always upload
          mcpData.changeType = "new";
          toUpload.push(mcpData);
        }
      }

      console.log("[BulkUpload] Diff complete:", {
        mode: updateMode,
        toUpload: toUpload.length,
        toUpdateCode: toUpdateCode.length,
        toUpdateReqs: toUpdateReqs.length,
        toUpdateBoth: toUpdateBoth.length,
        skipped: skipped.length,
      });

      setUploadState((prev) => ({
        ...prev,
        status: "ready",
        registry,
        folderHandle: dirHandle,
        toUpload,
        toUpdateCode,
        toUpdateReqs,
        toUpdateBoth,
        skipped,
        dbHashes,
        completed: [],
        failed: [],
        currentIndex: 0,
      }));
    } catch (error) {
      // User cancelled the picker
      if (error.name === "AbortError") {
        setUploadState((prev) => ({ ...prev, status: "idle" }));
        return;
      }

      console.error("[BulkUpload] Error:", error);
      setUploadState((prev) => ({
        ...prev,
        status: "idle",
        error: error.message,
      }));
      if (showMessage) {
        showMessage("error", error.message);
      }
    }
  };

  // Get last folder name for display hint
  const lastFolderName = localStorage.getItem(LAST_FOLDER_KEY);

  // Calculate progress - account for all update modes
  const calculateTotalToUpload = () => {
    if (uploadState.forceUpdate) {
      return uploadState.skipped.length;
    }
    if (updateMode === UPDATE_MODES.SMART_UPDATE) {
      // Smart mode uploads: new + code changes + both changes
      return (
        uploadState.toUpload.length +
        uploadState.toUpdateCode.length +
        uploadState.toUpdateBoth.length
      );
    }
    if (updateMode === UPDATE_MODES.CODE_ONLY) {
      return uploadState.toUpdateCode.length;
    }
    if (updateMode === UPDATE_MODES.REQUIREMENTS_ONLY) {
      return uploadState.toUpdateReqs.length;
    }
    // NEW_ONLY or FULL_UPDATE
    return uploadState.toUpload.length;
  };

  const totalToUpload = calculateTotalToUpload();
  const progressPercent =
    totalToUpload > 0
      ? Math.round((uploadState.currentIndex / totalToUpload) * 100)
      : 0;

  return (
    <div className="bulk-upload-zone" style={{ marginBottom: "30px" }}>
      <h4 style={{ marginBottom: "15px" }}>📦 Bulk Upload from MCP Maker</h4>

      {/* Update Mode Selector */}
      {uploadState.status === "idle" && (
        <div
          style={{
            marginBottom: "15px",
            padding: "12px",
            background: "#1a2332",
            borderRadius: "8px",
            border: "1px solid #2d3748",
          }}
        >
          <label
            style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}
          >
            Update Mode:
          </label>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                cursor: "pointer",
              }}
            >
              <input
                type="radio"
                name="updateMode"
                value={UPDATE_MODES.NEW_ONLY}
                checked={updateMode === UPDATE_MODES.NEW_ONLY}
                onChange={(e) => setUpdateMode(e.target.value)}
              />
              <span>New Only</span>
              <span style={{ color: "var(--text-secondary)", fontSize: "12px" }}>
                (skip existing)
              </span>
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                cursor: "pointer",
              }}
            >
              <input
                type="radio"
                name="updateMode"
                value={UPDATE_MODES.SMART_UPDATE}
                checked={updateMode === UPDATE_MODES.SMART_UPDATE}
                onChange={(e) => setUpdateMode(e.target.value)}
              />
              <span>🧠 Smart Update</span>
              <span style={{ color: "var(--accent-success)", fontSize: "12px" }}>
                (compare hashes)
              </span>
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                cursor: "pointer",
              }}
            >
              <input
                type="radio"
                name="updateMode"
                value={UPDATE_MODES.REQUIREMENTS_ONLY}
                checked={updateMode === UPDATE_MODES.REQUIREMENTS_ONLY}
                onChange={(e) => setUpdateMode(e.target.value)}
              />
              <span>Requirements Only</span>
              <span style={{ color: "var(--text-secondary)", fontSize: "12px" }}>
                (fast fix)
              </span>
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                cursor: "pointer",
              }}
            >
              <input
                type="radio"
                name="updateMode"
                value={UPDATE_MODES.CODE_ONLY}
                checked={updateMode === UPDATE_MODES.CODE_ONLY}
                onChange={(e) => setUpdateMode(e.target.value)}
              />
              <span>Code Only</span>
              <span style={{ color: "var(--text-secondary)", fontSize: "12px" }}>
                (server.py)
              </span>
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                cursor: "pointer",
              }}
            >
              <input
                type="radio"
                name="updateMode"
                value={UPDATE_MODES.FULL_UPDATE}
                checked={updateMode === UPDATE_MODES.FULL_UPDATE}
                onChange={(e) => setUpdateMode(e.target.value)}
              />
              <span>Full Update</span>
              <span style={{ color: "var(--text-secondary)", fontSize: "12px" }}>
                (new versions)
              </span>
            </label>
          </div>
        </div>
      )}

      {/* Error display */}
      {uploadState.error && (
        <div
          style={{
            background: "#3d1a1a",
            border: "1px solid #d32f2f",
            borderRadius: "8px",
            padding: "15px",
            marginBottom: "15px",
            color: "#ff8a80",
          }}
        >
          ❌ {uploadState.error}
        </div>
      )}

      {/* RESTORED STATE - Prompt user to resume or discard */}
      {uploadState.status === "restored" && (
        <div
          style={{
            background: "#2a2a1a",
            border: "2px solid var(--accent-warning)",
            borderRadius: "12px",
            padding: "25px",
            textAlign: "center",
            marginBottom: "15px",
          }}
        >
          <div style={{ fontSize: "48px", marginBottom: "15px" }}>⚠️</div>
          <div
            style={{
              fontSize: "18px",
              color: "var(--text-primary)",
              marginBottom: "10px",
              fontWeight: "bold",
            }}
          >
            Previous Upload Found
          </div>
          <div
            style={{ fontSize: "14px", color: "var(--accent-warning)", marginBottom: "15px" }}
          >
            A bulk upload was in progress but was interrupted.
            <br />
            {uploadState.completed?.length || 0} completed,{" "}
            {uploadState.toUpload?.length - (uploadState.currentIndex || 0) ||
              0}{" "}
            remaining, {uploadState.failed?.length || 0} failed
          </div>
          <div
            style={{ fontSize: "12px", color: "var(--text-secondary)", marginBottom: "20px" }}
          >
            ⚠️ Uploads require the original folder to be re-selected to resume.
            <br />
            If you don't have access to the folder, discard and start fresh.
          </div>
          <div
            style={{ display: "flex", gap: "15px", justifyContent: "center" }}
          >
            <button
              onClick={discardRestoredProgress}
              style={{
                background: "#d32f2f",
                border: "none",
                color: "var(--text-primary)",
                padding: "12px 24px",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              🗑️ Discard & Start Fresh
            </button>
            <button
              onClick={resetUpload}
              style={{
                background: "#2196F3",
                border: "none",
                color: "var(--text-primary)",
                padding: "12px 24px",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              📁 Select Folder to Resume
            </button>
          </div>
        </div>
      )}

      {/* IDLE STATE - Drop Zone */}
      {uploadState.status === "idle" && (
        <div
          onDrop={handleFolderDrop}
          onDragOver={handleDragOver}
          onClick={handleClickToBrowse}
          style={{
            border: "2px dashed var(--border-primary)",
            borderRadius: "12px",
            padding: "40px",
            textAlign: "center",
            background: "var(--bg-secondary)",
            cursor: "pointer",
            transition: "all 0.2s",
          }}
          onDragEnter={(e) => {
            e.currentTarget.style.borderColor = "var(--accent-success)";
            e.currentTarget.style.background = "#1a2a1a";
          }}
          onDragLeave={(e) => {
            e.currentTarget.style.borderColor = "var(--border-primary)";
            e.currentTarget.style.background = "var(--bg-secondary)";
          }}
        >
          <div style={{ fontSize: "48px", marginBottom: "15px" }}>📁</div>
          <div
            style={{ fontSize: "16px", color: "var(--text-primary)", marginBottom: "10px" }}
          >
            Drop MCP Maker folder here or click to browse
          </div>
          <div style={{ fontSize: "13px", color: "var(--text-secondary)", marginBottom: "8px" }}>
            Use <strong style={{ color: "var(--accent-primary)" }}>/stable</strong> for
            production (MAIN) · Use{" "}
            <strong style={{ color: "var(--accent-warning)" }}>/output</strong> for staging
          </div>
          <div style={{ fontSize: "12px", color: "var(--text-tertiary)", marginBottom: "8px" }}>
            Folder should contain registry.json and MCP subfolders
          </div>
          {lastFolderName && (
            <div style={{ fontSize: "12px", color: "var(--text-tertiary)" }}>
              Last used: {lastFolderName}
            </div>
          )}
        </div>
      )}

      {/* PARSING/DIFFING STATE */}
      {(uploadState.status === "parsing" ||
        uploadState.status === "diffing") && (
        <div
          style={{
            background: "#1a1a2a",
            border: "1px solid #2196F3",
            borderRadius: "12px",
            padding: "30px",
            textAlign: "center",
          }}
        >
          <div
            style={{ fontSize: "32px", marginBottom: "15px" }}
            className="spin"
          >
            ⏳
          </div>
          <div style={{ color: "var(--text-primary)" }}>
            {uploadState.status === "parsing"
              ? "Reading folder contents..."
              : "Comparing against existing MCPs..."}
          </div>
        </div>
      )}

      {/* READY STATE - Preview */}
      {uploadState.status === "ready" && (
        <div
          style={{
            background: "#1a2a1a",
            border: "1px solid var(--accent-success)",
            borderRadius: "12px",
            padding: "20px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
            }}
          >
            <div>
              <div style={{ fontSize: "24px", color: "var(--accent-success)" }}>
                ✅ Ready to Upload
              </div>
              <div style={{ color: "var(--text-secondary)", marginTop: "5px" }}>
                Registry v{uploadState.registry?.version || "unknown"}
              </div>
            </div>
            <button
              onClick={resetUpload}
              style={{
                background: "transparent",
                border: "1px solid var(--border-primary)",
                color: "var(--text-secondary)",
                padding: "8px 15px",
                borderRadius: "6px",
                cursor: "pointer",
              }}
            >
              ✕ Cancel
            </button>
          </div>

          {/* Counts - Smart Update breakdown */}
          <div
            style={{
              display: "flex",
              gap: "15px",
              marginBottom: "20px",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                background: "#2a3a2a",
                padding: "12px 20px",
                borderRadius: "8px",
                textAlign: "center",
                minWidth: "80px",
              }}
            >
              <div style={{ fontSize: "24px", color: "var(--accent-success)" }}>
                {uploadState.toUpload.length}
              </div>
              <div style={{ color: "var(--text-secondary)", fontSize: "12px" }}>New</div>
            </div>
            {updateMode === UPDATE_MODES.SMART_UPDATE && (
              <>
                <div
                  style={{
                    background: "#2a2a3a",
                    padding: "12px 20px",
                    borderRadius: "8px",
                    textAlign: "center",
                    minWidth: "80px",
                  }}
                >
                  <div style={{ fontSize: "24px", color: "var(--accent-primary)" }}>
                    {uploadState.toUpdateCode?.length || 0}
                  </div>
                  <div style={{ color: "var(--text-secondary)", fontSize: "12px" }}>
                    Code Changed
                  </div>
                </div>
                <div
                  style={{
                    background: "#3a2a2a",
                    padding: "12px 20px",
                    borderRadius: "8px",
                    textAlign: "center",
                    minWidth: "80px",
                  }}
                >
                  <div style={{ fontSize: "24px", color: "var(--accent-warning)" }}>
                    {uploadState.toUpdateReqs?.length || 0}
                  </div>
                  <div style={{ color: "var(--text-secondary)", fontSize: "12px" }}>
                    Reqs Changed
                  </div>
                </div>
                <div
                  style={{
                    background: "#3a2a3a",
                    padding: "12px 20px",
                    borderRadius: "8px",
                    textAlign: "center",
                    minWidth: "80px",
                  }}
                >
                  <div style={{ fontSize: "24px", color: "var(--accent-primary)" }}>
                    {uploadState.toUpdateBoth?.length || 0}
                  </div>
                  <div style={{ color: "var(--text-secondary)", fontSize: "12px" }}>
                    Both Changed
                  </div>
                </div>
              </>
            )}
            <div
              style={{
                background: "var(--bg-tertiary)",
                padding: "12px 20px",
                borderRadius: "8px",
                textAlign: "center",
                minWidth: "80px",
              }}
            >
              <div style={{ fontSize: "24px", color: "var(--text-secondary)" }}>
                {uploadState.skipped.length}
              </div>
              <div style={{ color: "var(--text-tertiary)", fontSize: "12px" }}>
                {updateMode === UPDATE_MODES.SMART_UPDATE
                  ? "Unchanged"
                  : "Skipped"}
              </div>
            </div>
          </div>

          {/* Force update checkbox */}
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              color: "var(--text-secondary)",
              marginBottom: "20px",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={uploadState.forceUpdate}
              onChange={toggleForceUpdate}
              style={{ width: "18px", height: "18px" }}
            />
            <span>Force update existing MCPs (will overwrite)</span>
            {uploadState.forceUpdate && (
              <span style={{ color: "var(--accent-warning)" }}>
                ⚠️ +{uploadState.skipped.length} to upload
              </span>
            )}
          </label>

          {/* Upload button */}
          <button
            onClick={startUpload}
            style={{
              background: "var(--accent-success)",
              border: "none",
              color: "var(--text-primary)",
              padding: "15px 30px",
              borderRadius: "8px",
              fontSize: "16px",
              cursor: "pointer",
              width: "100%",
              fontWeight: "bold",
            }}
          >
            📤 Upload {totalToUpload} MCPs
          </button>
        </div>
      )}

      {/* UPLOADING STATE */}
      {(uploadState.status === "uploading" ||
        uploadState.status === "paused") && (
        <div
          style={{
            background: "#1a1a2a",
            border: "1px solid #2196F3",
            borderRadius: "12px",
            padding: "20px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "15px",
            }}
          >
            <div style={{ fontSize: "18px", color: "var(--accent-primary)" }}>
              {uploadState.status === "paused"
                ? "⏸️ Paused"
                : "📤 Uploading..."}
            </div>
            <div style={{ color: "var(--text-secondary)" }}>
              {uploadState.currentIndex} / {totalToUpload}
            </div>
          </div>

          {/* Progress bar */}
          <div
            style={{
              background: "#2a2a3a",
              borderRadius: "8px",
              height: "24px",
              overflow: "hidden",
              marginBottom: "15px",
            }}
          >
            <div
              style={{
                background: "linear-gradient(90deg, #2196F3, var(--accent-success))",
                height: "100%",
                width: `${progressPercent}%`,
                transition: "width 0.3s",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--text-primary)",
                fontSize: "12px",
                fontWeight: "bold",
              }}
            >
              {progressPercent}%
            </div>
          </div>

          {/* Current MCP */}
          {uploadState.currentMcpName && (
            <div
              style={{ color: "var(--text-secondary)", marginBottom: "15px", fontSize: "14px" }}
            >
              Current: {uploadState.currentMcpName}
            </div>
          )}

          {/* Stats */}
          <div style={{ display: "flex", gap: "20px", marginBottom: "20px" }}>
            <div style={{ color: "var(--accent-success)" }}>
              ✅ {uploadState.completed.length} succeeded
            </div>
            <div style={{ color: "var(--accent-danger)" }}>
              ❌ {uploadState.failed.length} failed
            </div>
          </div>

          {/* Controls */}
          <div style={{ display: "flex", gap: "10px" }}>
            {uploadState.status === "uploading" ? (
              <button
                onClick={pauseUpload}
                style={{
                  background: "var(--accent-warning)",
                  border: "none",
                  color: "var(--text-primary)",
                  padding: "10px 20px",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                ⏸️ Pause
              </button>
            ) : (
              <button
                onClick={resumeUpload}
                style={{
                  background: "var(--accent-success)",
                  border: "none",
                  color: "var(--text-primary)",
                  padding: "10px 20px",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                ▶️ Resume
              </button>
            )}
            <button
              onClick={resetUpload}
              style={{
                background: "transparent",
                border: "1px solid var(--border-primary)",
                color: "var(--text-secondary)",
                padding: "10px 20px",
                borderRadius: "6px",
                cursor: "pointer",
              }}
            >
              ✕ Cancel
            </button>
          </div>
        </div>
      )}

      {/* COMPLETE STATE */}
      {uploadState.status === "complete" && (
        <div
          style={{
            background: uploadState.failed.length > 0 ? "#2a2a1a" : "#1a2a1a",
            border: `1px solid ${uploadState.failed.length > 0 ? "var(--accent-warning)" : "var(--accent-success)"}`,
            borderRadius: "12px",
            padding: "20px",
          }}
        >
          <div
            style={{
              fontSize: "24px",
              color: uploadState.failed.length > 0 ? "var(--accent-warning)" : "var(--accent-success)",
              marginBottom: "15px",
            }}
          >
            {uploadState.failed.length > 0
              ? "⚠️ Upload Complete with Errors"
              : "✅ Upload Complete!"}
          </div>

          {/* Summary */}
          <div style={{ display: "flex", gap: "20px", marginBottom: "20px" }}>
            <div
              style={{
                background: "#2a3a2a",
                padding: "15px 25px",
                borderRadius: "8px",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "28px", color: "var(--accent-success)" }}>
                {uploadState.completed.length}
              </div>
              <div style={{ color: "var(--text-secondary)", fontSize: "13px" }}>Uploaded</div>
            </div>
            <div
              style={{
                background: "#3a2a2a",
                padding: "15px 25px",
                borderRadius: "8px",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "28px", color: "var(--accent-danger)" }}>
                {uploadState.failed.length}
              </div>
              <div style={{ color: "var(--text-secondary)", fontSize: "13px" }}>Failed</div>
            </div>
            <div
              style={{
                background: "var(--bg-tertiary)",
                padding: "15px 25px",
                borderRadius: "8px",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: "28px", color: "var(--text-secondary)" }}>
                {uploadState.skipped.length}
              </div>
              <div style={{ color: "var(--text-tertiary)", fontSize: "13px" }}>Skipped</div>
            </div>
          </div>

          {/* Failed items */}
          {uploadState.failed.length > 0 && (
            <div style={{ marginBottom: "20px" }}>
              <div
                style={{
                  color: "var(--accent-danger)",
                  marginBottom: "10px",
                  fontWeight: "bold",
                }}
              >
                Failed MCPs:
              </div>
              <div
                style={{
                  maxHeight: "200px",
                  overflowY: "auto",
                  background: "var(--bg-secondary)",
                  borderRadius: "8px",
                  padding: "10px",
                }}
              >
                {uploadState.failed.map((mcp, idx) => (
                  <div
                    key={mcp.key}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px",
                      borderBottom:
                        idx < uploadState.failed.length - 1
                          ? "1px solid var(--border-primary)"
                          : "none",
                    }}
                  >
                    <div>
                      <span style={{ color: "var(--text-primary)" }}>
                        {mcp.name || mcp.key}
                      </span>
                      <span
                        style={{
                          color: "var(--accent-danger)",
                          fontSize: "12px",
                          marginLeft: "10px",
                        }}
                      >
                        {mcp.error}
                      </span>
                    </div>
                    <button
                      onClick={() => retrySingleMcp(mcp, idx)}
                      style={{
                        background: "#2196F3",
                        border: "none",
                        color: "var(--text-primary)",
                        padding: "5px 10px",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "12px",
                      }}
                    >
                      🔄 Retry
                    </button>
                  </div>
                ))}
              </div>

              {/* Retry all button */}
              <button
                onClick={retryAllFailed}
                style={{
                  background: "var(--accent-danger)",
                  border: "none",
                  color: "var(--text-primary)",
                  padding: "10px 20px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  marginTop: "10px",
                }}
              >
                🔄 Retry All Failed ({uploadState.failed.length})
              </button>
            </div>
          )}

          {/* Done button */}
          <button
            onClick={resetUpload}
            style={{
              background: "var(--accent-success)",
              border: "none",
              color: "var(--text-primary)",
              padding: "12px 25px",
              borderRadius: "6px",
              cursor: "pointer",
              width: "100%",
            }}
          >
            ✓ Done
          </button>
        </div>
      )}

      {/* CSS for spin animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
          display: inline-block;
        }
      `}</style>
    </div>
  );
};

export default BulkUploadZone;
